import React, { Suspense, useCallback, useEffect, useState } from "react";
import AssetInformationGrid from "@components/grid/AssetInformationGrid";
import { useSetRecoilState, useRecoilValue, useRecoilState } from "recoil";
import {
  fundOperationListState,
  paginationState,
  selectedRowState,
  fundSearchTextState,
  enableFilterState,
  familyTickerFilterState,
  fundKrQueryFilterState,
  fundKrSelectedQueryFilterState,
  fundKrSelectedClass,
  fundKrSortState,
  FUND_KR_DEFAULT_SORT
} from "@states/fundKr";

import { COLUMNS, FUNDKR_SORT_FIELD } from "../../define";
import { Toolbar } from "./Toolbar";
import _ from "lodash";
import { Grid, Checkbox, FormControlLabel } from "@mui/material";

function AssetViewerTable({ onChangeChecked, searchText, onSelectAsset }) {
  const setSelectedRow = useSetRecoilState(selectedRowState);
  const setPagination = useSetRecoilState(paginationState);
  const setSearchText = useSetRecoilState(fundSearchTextState);

  useEffect(() => {
    requestDebounce(searchText);
  }, [searchText]);

  const requestDebounce = useCallback(
    _.throttle((text) => {
      text = text.trim();
      if (!text) {
        setSearchText(undefined);
      } else if (text && text.length >= 2) {
        setSearchText(text);
      }
    }, 1500),
    [searchText]
  );

  const onPageChange = (page) => {
    setPagination((prev) => ({ ...prev, page: page + 1 }));
  };
  const onPageSizeChange = (pageSize) => {
    setPagination((prev) => ({ ...prev, page_size: pageSize }));
  };

  const assetFundOperation = useRecoilValue(fundOperationListState);
  const [sort, setSort] = useRecoilState(fundKrSortState);

  // 그리드는 화면 컬럼 이름(한글)으로 말하고 서버는 DB 컬럼명으로 듣는다. 양방향으로 번역한다.
  const sortModel = React.useMemo(() => {
    const field = Object.keys(FUNDKR_SORT_FIELD).find(
      (key) => FUNDKR_SORT_FIELD[key] === sort.field
    );
    return field ? [{ field, sort: sort.order }] : [];
  }, [sort]);

  const onSortModelChange = (model) => {
    // 정렬을 바꾸면 순서가 통째로 달라진다. 지금 페이지에 머무르면 방금 누른 것과
    // 상관없는 구간을 보게 되므로 첫 페이지로 돌린다.
    setPagination((prev) => ({ ...prev, page: 1 }));
    const next = model[0];
    const column = next ? FUNDKR_SORT_FIELD[next.field] : undefined;
    setSort(column && next?.sort ? { field: column, order: next.sort } : FUND_KR_DEFAULT_SORT);
  };

  const params = {
    hideFooter: false,
    pagination: true,
    paginationMode: "server",
    onPageChange,
    onPageSizeChange,
    rowCount: assetFundOperation.total_count,
    // ⚠️ 정렬을 **서버로** 보낸다. 29,184 건을 100 씩 받아 오므로 화면에서 정렬하면
    //    지금 보고 있는 100 건만 줄을 선다 (실측: 헤더를 눌러도 요청이 안 나갔다).
    //    `AssetInformationGrid` 는 이 객체를 그대로 그리드에 펼치므로, 공용 컴포넌트를
    //    건드리지 않고 펀드 화면에만 적용된다 — ETF·주식·지수·부동산은 전량을 받아 오니
    //    지금처럼 화면에서 정렬하는 것이 맞다.
    sortingMode: "server",
    sortModel,
    onSortModelChange,
    // 해제 상태를 만들지 않는다. 서버에는 "무정렬" 이 없어서 해제하면 기본 정렬로
    // 돌아가는데, 기본이 걸린 칸을 누르면 아무 일도 안 일어나 고장으로 보인다.
    sortingOrder: ["asc", "desc"],
  };

  return (
    <>
      <AssetInformationGrid
        market={"FUNDKR"}
        onSelectAsset={(selected) => {
          setSelectedRow(selected);
          if (onSelectAsset) {
            onSelectAsset(selected);
          }
        }}
        columns={COLUMNS.FUNDKR}
        data={assetFundOperation.data}
        pagination={params}
        onChangeChecked={onChangeChecked}
      />
    </>
  );
}

export const WrappedComponent = (props) => {
  const [searchText, setSearch] = useState("");
  const [enableFilter, setEnableFilter] = useRecoilState(enableFilterState);
  const [familyTickerFilter, setFamilyTickerFilter] = useRecoilState(familyTickerFilterState);
  const setQueryFilter = useSetRecoilState(fundKrQueryFilterState);
  const setSelectedEtfTicker = useSetRecoilState(fundKrSelectedQueryFilterState);
  const setRadioValue = useSetRecoilState(fundKrSelectedClass)

  return (
    <>
      <Grid container spacing={2} justifyContent="space-between" alignItems="center">
        <Grid item xs={12} md={4}>
          <Toolbar searchText={searchText} setSearchText={setSearch} />
        </Grid>
        <Grid item xs={12} sm container>
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableFilter}
                  onChange={(e) => setEnableFilter(e.target.checked)}
                />
              }
              label="상세검색"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={familyTickerFilter}
                  onChange={(e) => {
                    setQueryFilter({});
                    setSelectedEtfTicker({});
                    setRadioValue({});
                    setFamilyTickerFilter(e.target.checked);
                  }}
                />
              }
              label="모펀드만 보기"
            />
          </Grid>
        </Grid>
      </Grid>
      <Suspense
        fallback={
          <AssetInformationGrid
            market={"FUNDKR"}
            columns={COLUMNS.FUNDKR}
            loading
            data={{ data: [], total_count: 0 }}
            onSelectAsset={undefined}
            onChangeChecked={undefined}
          />
        }
      >
        <AssetViewerTable
          {...props}
          searchText={searchText}
          onChangeChecked={props.onChangeChecked}
          onSelectAsset={props.onSelectAsset}
        />
      </Suspense>
    </>
  );
};

export default WrappedComponent;
